import { db } from '../firebase'
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore'

// Save device info to the Users collection (capital U to match backend)
export const saveUserDevice = async (firebaseUid, deviceId) => {
  if (!firebaseUid) {
    console.error('❌ FirebaseService: No firebaseUid provided');
    return false;
  }
  
  if (!deviceId) {
    console.error('❌ FirebaseService: No deviceId provided');
    return false;
  }
  
  try {
    const deviceUpdateData = {
      deviceId: deviceId,
      deviceRegisteredAt: new Date().toISOString(),
      isDeviceRestricted: true,
      deviceResetAt: null
    };
    
    const userDocRef = doc(db, 'Users', firebaseUid);
    
    const docSnap = await getDoc(userDocRef);
    if (!docSnap.exists()) {
      console.error('❌ FirebaseService: User document not found');
      return false;
    }
    
    await updateDoc(userDocRef, deviceUpdateData);
    
    const verification = await getDoc(userDocRef);
    if (!verification.exists()) {
      console.error('❌ FirebaseService: Verification failed - document not found');
    }
    
    return true;
  } catch (error) {
    console.error('❌ FirebaseService: Error saving user device:', error.message);
    return false;
  }
}

export const getUserDevice = async (firebaseUid) => {
  if (!firebaseUid) {
    console.error('❌ FirebaseService: No firebaseUid provided');
    return null;
  }
  
  try {
    const docRef = doc(db, 'Users', firebaseUid);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const userData = docSnap.data();
      
      if (userData.deviceId) {
        const deviceData = {
          deviceId: userData.deviceId,
          registeredAt: userData.deviceRegisteredAt,
          isRestricted: userData.isDeviceRestricted || true,
          resetAt: userData.deviceResetAt || null
        };
        return deviceData;
      } else {
        console.log('⚠️ FirebaseService: User found but no device data');
        return null;
      }
    } else {
      console.log('⚠️ FirebaseService: User document not found in Users collection');
      return null;
    }
  } catch (error) {
    console.error('❌ FirebaseService: Error getting user device:', error.message);
    return null;
  }
}

export const resetUserDevice = async (firebaseUid) => {
  if (!firebaseUid) {
    console.error('❌ FirebaseService: No firebaseUid provided');
    return false;
  }
  
  try {
    const currentData = await getUserDevice(firebaseUid);
    
    const resetData = {
      deviceId: null,
      deviceResetAt: new Date().toISOString(),
      isDeviceRestricted: true,
      previousDeviceId: currentData?.deviceId || null
    };
    
    const docRef = doc(db, 'Users', firebaseUid);
    await updateDoc(docRef, resetData);
    
    const verification = await getDoc(docRef);
    if (verification.exists()) {
      const data = verification.data();
      console.log('✅ FirebaseService: Reset verification:', {
        deviceId: data.deviceId,
        deviceResetAt: data.deviceResetAt,
        previousDeviceId: data.previousDeviceId
      });
    }
    
    return true;
  } catch (error) {
    console.error('❌ FirebaseService: Error resetting user device:', error.message);
    return false;
  }
}

// Function to get all users with their device info for admin panel
export const getAllUsersWithDevices = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'Users'));
    
    const usersWithDevices = {};
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      const userId = doc.id;
      
      const deviceInfo = {
        hasDevice: !!userData.deviceId,
        deviceId: userData.deviceId || null,
        registeredAt: userData.deviceRegisteredAt || null,
        isRestricted: userData.isDeviceRestricted || true,
        resetAt: userData.deviceResetAt || null,
        name: userData.name,
        email: userData.email,
        role: userData.role,
        employeeId: userData.employeeId
      };
      
      usersWithDevices[userId] = deviceInfo;
    });
    
    return usersWithDevices;
  } catch (error) {
    console.error('❌ FirebaseService: Error getting all users with devices:', error.message);
    return {};
  }
}

// Function to find user by employeeId and reset device
export const resetUserDeviceByEmployeeId = async (employeeId) => {
  try {
    const usersRef = collection(db, 'Users');
    const q = query(usersRef, where('employeeId', '==', employeeId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.error('❌ FirebaseService: No user found with employeeId:', employeeId);
      return false;
    }
    
    const userDoc = querySnapshot.docs[0];
    const userId = userDoc.id;
    
    const resetResult = await resetUserDevice(userId);
    return resetResult;
  } catch (error) {
    console.error('❌ FirebaseService: Error resetting device by employeeId:', error.message);
    return false;
  }
} 